import { useQuery, useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { 
    cancelAppointment,
    createAppointment, 
    getAppointments, 
    getAppointmentsCalender, 
    getPatients, 
    getUpcomingAppointments, 
    getUserAppointment, 
    getUserAppointmentByPhone, 
    rescheduleAppointment
} from "../../api/appointment/appointmentApi";
import { queryClient } from "../../../lib/query";


export const useGetAppointmentsCalender = (
    params?: { 
        appointment_date?: string 
    }) => {
    return useQuery({
        queryKey: ["appointments", params],
        queryFn: async () => {
            const response = await getAppointmentsCalender(params);
            return response;
        },
        staleTime: 0, 
        gcTime: 0, 
        refetchOnWindowFocus: true,
        refetchOnMount: true, 
    });
};

export const useGetAppointments = (
    params?: { 
        limit?: number, 
        page?: number
    }) => {
    return useQuery({
        queryKey: ["appointments", params],
        queryFn: async () => {
            const response = await getAppointments(params);
            return response;
        },
        staleTime: 0, // Make data immediately stale so it refetches
        gcTime: 0, // Don't cache for long
        refetchOnWindowFocus: true, // Refetch when window gains focus
        refetchOnMount: true, // Always refetch on mount
    });
};
    
export const useGetUpcomingAppointments = (
    params?: { 
        limit?: number, 
        page?: number
    }) => {
    return useQuery({
        queryKey: ["appointments", params],
        queryFn: async () => {
            const response = await getUpcomingAppointments(params);
            return response;
        },
        staleTime: 0, // Make data immediately stale so it refetches
        gcTime: 0, // Don't cache for long
        refetchOnWindowFocus: true, // Refetch when window gains focus
        refetchOnMount: true, // Always refetch on mount
    });
};

export const useGetUserAppointment = (userId: string | number) => {
    return useQuery({
        queryKey: ["user-appointment", userId],
        queryFn: async () => {
            const response = await getUserAppointment(userId);
            return response;
        },
        staleTime: 1000 * 30, // 30 seconds
    });
};

export const useCreateAppointment = () => {
    
    return useMutation({
        mutationKey: ["create-appointment"],
        mutationFn: async (payload: {
            patient_name: string,
            patient_phone: string,
            reason: string,
            appointment_datetime: string,
            duration_minutes: number
        }) => {
            const response = await createAppointment(payload);
            return response;
        },

        onSuccess: async () => {
            console.log('Appointment created successfully, invalidating queries...');
            
            // Add a small delay to ensure backend processing is complete
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Clear the cache completely first
            queryClient.clear();
            
            // Then invalidate all appointment queries
            await queryClient.invalidateQueries({ 
                queryKey: ["appointments"],
                exact: false 
            });
            
            await queryClient.invalidateQueries({ 
                queryKey: ["user-appointment"],
                exact: false
            });
            
            await queryClient.invalidateQueries({ 
                queryKey: ["user-appointment-phone"],
                exact: false
            });

            console.log('Query invalidation completed - cache cleared and queries invalidated');
        },
        onError: (error) => {
            console.error('Failed to create appointment:', error);
        },
    });
};

export const useRescheduleAppointment = () => {
    return useMutation({
        mutationKey: ["reschedule-appointment"],
        mutationFn: async (payload: { appointmentId: string | number, appointment_datetime: string }) => {
            const response = await rescheduleAppointment(payload.appointmentId, payload.appointment_datetime);
            return response;
        },

        onSuccess: async () => {
            console.log('Appointment rescheduled successfully, invalidating queries...');
            
            // Add a small delay to ensure backend processing is complete
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Clear the cache completely first
            queryClient.clear();
            
            // Then invalidate all appointment queries
            await queryClient.invalidateQueries({ 
                queryKey: ["appointments"],
                exact: false 
            });
            
            await queryClient.invalidateQueries({ 
                queryKey: ["user-appointment"],
                exact: false
            });
            
            await queryClient.invalidateQueries({ 
                queryKey: ["user-appointment-phone"],
                exact: false
            });

            console.log('Query invalidation completed - cache cleared and queries invalidated');
        },
        onError: (error) => {
            console.error('Failed to reschedule appointment:', error);
        },
    });
};

export const useCancelAppointment = () => {
    return useMutation({
        mutationKey: ["cancel-appointment"],
        mutationFn: async (payload: { appointmentId: string | number }) => {
            const response = await cancelAppointment(payload.appointmentId);
            return response;
        },

        onSuccess: async () => {
            console.log('Appointment canceled successfully, invalidating queries...');
            
            // Add a small delay to ensure backend processing is complete
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Clear the cache completely first
            queryClient.clear();
            
            // Then invalidate all appointment queries
            await queryClient.invalidateQueries({ 
                queryKey: ["appointments"],
                exact: false 
            });
            
            await queryClient.invalidateQueries({ 
                queryKey: ["user-appointment"],
                exact: false
            });
            
            await queryClient.invalidateQueries({ 
                queryKey: ["user-appointment-phone"],
                exact: false
            });

            console.log('Query invalidation completed - cache cleared and queries invalidated');
        },
        onError: (error) => {
            console.error('Failed to cancel appointment:', error);
        },
    });
};

export const useGetUserAppointmentByPhone = (phone: string | number) => {
    return useQuery({
        queryKey: ["user-appointment-phone", phone],
        queryFn: async () => {
            const response = await getUserAppointmentByPhone(phone);
            return response;
        },
        staleTime: 1000 * 30, // 30 seconds
    });
};



export const useGetPatients = () => {
    return useQuery({
        queryKey: ["users"],
        queryFn: async () => {
            const response = await getPatients();
            return response;
        },
        staleTime: 1000 * 30, // 30 seconds
    });
};

export const useGetPatientsInfinite = (limit: number = 10) => {
    return useInfiniteQuery({
        queryKey: ["users-infinite", limit],
        queryFn: async ({ pageParam = 1 }) => {
            const response = await getPatients({
                page: pageParam,
                limit: limit
            });
            return response;
        },
        getNextPageParam: (lastPage) => {
            // Use the pagination metadata from the API response
            if (lastPage.pagination?.has_next_page) {
                return lastPage.pagination.next_page;
            }
            return undefined;
        },
        initialPageParam: 1,
        staleTime: 1000 * 30, // 30 seconds
    });
};

